import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth, useLoginWithRedirect } from '@frontegg/react'
import { ThemeProvider, Box } from '@mui/material'
import CssBaseline from '@mui/material/CssBaseline'
import axios from '../../lib/axios'
import { QueryClientProvider } from 'react-query'
import { queryClient } from 'lib/queryClient'
import { ReactQueryDevtools } from 'react-query/devtools'
import theme from 'theme'
import { Toaster } from 'react-hot-toast'
import LogRocket from 'logrocket'

/* Utils */
import { buildMuiTheme } from '../../theme/muiTheme'
import { UserConfiguredIntegration } from '../../models'
import { runAccountTransition, shouldReloadAccount } from 'lib/accountSession'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { modalPropsSelector, ModalType, ModalVariant } from '../../store/modals/modalSlice'
import { closeNotification, notificationPropsSelector } from '../../store/notifications/notificationSlice'
import { selectThemeMode } from '../../store/theme/themeSlice'

/* Components */
import Modal from '../components/modal/Modal'
import MainLayout from '../MainLayout'
import Notification from '../components/notification/Notification'
import DeleteTicket from '../components/modal/variants/jira-ticket/components/DeleteTicket'
import JiraIntegration from '../components/modal/variants/jira-integration/JiraIntegration'
import InviteUser from '../components/modal/variants/invite-user/InviteUser'
import DeleteUsers from '../components/modal/variants/delete-users/DeleteUsers'
import DeleteAccount from '../components/modal/variants/delete-account/DeleteAccount'
import ChangeName from '../components/modal/variants/user-profile/sub-modals/ChangeName'
import ChangeEmail from '../components/modal/variants/user-profile/sub-modals/ChangeEmail'
import ChangeRole from '../components/modal/variants/user-profile/sub-modals/ChangeRole'
import TransferOwnership from '../components/modal/variants/user-profile/sub-modals/TransferOwnership'
import Auth0Integration from '../components/modal/variants/auth0-integration/Auth0Integration'
import SentinelIntegration from '../components/modal/variants/sentinel-integration/SentinelIntegration'
import DeleteIntegration from '../components/modal/variants/shared/DeleteIntegration'
import CrowdstrikeIntegration from '../components/modal/variants/crowdstrike-integration/CrowdstrikeIntegration'
import DeleteIntegrationInstance from '../components/modal/variants/delete-integration-instance/DeleteIntegrationInstance'
import DeleteApplicationInstance from '../components/modal/variants/delete-integration-instance/DeleteApplicationInstance'
import DeactivateCpi from '../components/modal/variants/deactivate-cpi/DeactivateCpi'
import DeselectNotification from '../components/modal/variants/deselect-notification/DeselectNotification'
import UserProfile from '../components/modal/variants/user-profile/UserProfile'
import ZoomIn from '../components/modal/variants/formula/ZoomIn'
import DeleteDeactivate from '../components/modal/variants/deactivate-cpi/DeleteDeactivateCPI'
import UpdateSSoModal from '../components/modal/variants/accont-managment/UpdateSSoModal'
import SSoVerificationFailed from '../components/modal/variants/accont-managment/SSoVerificationFailed'
import SSoDeleteNotification from '../components/modal/variants/accont-managment/SSoDeleteNotification'
import CancelChanges from '../components/modal/variants/weights/CancelChanges'
import RevertWeights from '../components/modal/variants/weights/RevertWeights'
import UpdateWeights from '../components/modal/variants/weights/UpdateWeights'
import LeaveSetupIntegrations from '../components/modal/variants/integrations/LeaveSetupIntegrations'
import ConfirmDeleteFramework from '../components/modal/variants/framework/ConfirmDeleteFramework'
import ConfirmResetFramework from '../components/modal/variants/framework/ConfirmResetFramework'
import DisconnectJiraProject from '../components/modal/variants/jira-integration/DisconnectJiraProject'
import AddCpisToIntegration from '../components/modal/variants/integrations/add-cpis-to-integrations/AddCpisToIntegration'
import ActivateCpiModal from '../components/modal/variants/activate-cpi/ActivateCpi'
import NexaAIToggleModal from '../components/modal/variants/nexa-ai/NexaAIToggleModal'
import CalculationDetails from '../components/modal/variants/calculation-details/CalculationDetails'
import BenchmarkingDetails from '../components/modal/variants/benchmarking-details/BenchmarkingDetails'
import CreateExclusionRule from '../components/modal/variants/exclusion-rule/CreateExclusionRule'
import ExcludedRecords from '../components/modal/variants/exclusion-rule/ExcludedRecords'
import CreateTicket from '../components/modal/variants/create-ticket/CreateTicket'
import DeleteExclusionRule from '../components/modal/variants/exclusion-rule/DeleteExclusionRule'
import { isIntegrationsSubPath } from '../pages/settings/integrations/integrationPaths'

// Frontegg's own storage key and hosted-login return route (@frontegg/redux-store constants and defaults).
const FRONTEGG_AFTER_AUTH_REDIRECT_URL = 'FRONTEGG_AFTER_AUTH_REDIRECT_URL'
const HOSTED_LOGIN_CALLBACK_PATH = '/oauth/callback'

const App = () => {
  const dispatch = useAppDispatch()
  const modalProps = useAppSelector(modalPropsSelector)
  const [configuredIntegrations, setConfiguredIntegrations] = useState<UserConfiguredIntegration[]>([])
  const { message, variant, open, autoHideDelay, title } = useAppSelector(notificationPropsSelector)
  const themeMode = useAppSelector(selectThemeMode)
  const muiTheme = useMemo(() => buildMuiTheme(themeMode), [themeMode])
  let content = <></>
  let sx = {}
  let paperStyles = {}
  let drawer = false
  const disableCloseOnClickOutside = false
  const { user, isAuthenticated } = useAuth()
  const loginWithRedirect = useLoginWithRedirect()
  const location = useLocation()
  const recentlyAddedVendors = window && typeof window !== 'undefined' && localStorage.getItem('recently_added_vendors')
  const costDistributionItems = window && typeof window !== 'undefined' && localStorage.getItem('cost_distribution')

  useEffect(() => {
    if (recentlyAddedVendors) {
      const parsed = JSON.parse(recentlyAddedVendors)
      if (Array.isArray(parsed) && parsed.length > 0) {
        const prunedItems = parsed.filter((n: any) => !!n.display_name && !!n.uuid && !!n.name)
        localStorage.setItem('recently_added_vendors', JSON.stringify(prunedItems))
      }
    }
  }, [])

  useEffect(() => {
    if (costDistributionItems) {
      const parsed = JSON.parse(costDistributionItems)
      if (Array.isArray(parsed) && parsed.length > 0) {
        const prunedCostDistributionItems: any[] = []

        parsed.forEach((item) => {
          const itemExists = prunedCostDistributionItems.find((n) => n.uuid === item.uuid)
          if (!itemExists) {
            prunedCostDistributionItems.push(item)
          }
        })

        localStorage.setItem('cost_distribution', JSON.stringify(prunedCostDistributionItems))
      }
    }
  }, [])

  const fetchConfiguredIntegrations = useCallback(async () => {
    const res = await axios.get('/api/v3/integrations/configured')

    if (res.status === 200) {
      const { data } = res.data
      setConfiguredIntegrations(data.integrations)
    }
  }, [])

  useEffect(() => {
    if ((modalProps && modalProps.type === 'mssentinel') || (modalProps && modalProps.type === 'crowdstrike')) {
      fetchConfiguredIntegrations().catch((e) => e)
    }
  }, [modalProps])

  useEffect(() => {
    if (open) {
      setTimeout(() => {
        dispatch(closeNotification())
      }, autoHideDelay)
    }
  }, [open])

  useEffect(() => {
    if (!isAuthenticated) {
      /* Frontegg returns here after hosted login but never records the page itself; without it a
       * session-less arrival (an OAuth callback in a private window) lands on the dashboard. */
      const { pathname, search } = window.location
      if (pathname !== HOSTED_LOGIN_CALLBACK_PATH) {
        try {
          localStorage.setItem(FRONTEGG_AFTER_AUTH_REDIRECT_URL, `${pathname}${search}`)
        } catch (e) {
          console.error(e)
        }
      }
      loginWithRedirect()
    }
  }, [isAuthenticated, loginWithRedirect])

  useEffect(() => {
    if (user && isAuthenticated && ['production', 'staging'].includes(process.env.REACT_APP_NODE_ENV as string)) {
      LogRocket.identify(user.id, {
        name: user.name,
        email: user.email
      })
    }
  }, [isAuthenticated, user])

  const previousTenantIdRef = useRef<string | null>(null)
  const [accountEpoch, setAccountEpoch] = useState(0)

  useEffect(() => {
    if (!isAuthenticated || !user) {
      // Reset on sign-out so the next sign-in is treated as a fresh login rather than an unchanged account.
      previousTenantIdRef.current = null
      return
    }

    const nextTenantId = (user.tenantId as string | undefined) ?? null
    const previousTenantId = previousTenantIdRef.current
    previousTenantIdRef.current = nextTenantId

    // One ordered path: signing in loads the account; switching accounts clears the old one, then loads the new.
    runAccountTransition(previousTenantId, nextTenantId).catch((e) => console.error(e))

    /* Move this on only after the transition above has cleared the old account, never on the account
     * id itself. It reloads whichever page the operator is on, and pages hold data of their own that
     * clearing the store cannot reach. A moment earlier and that page's new requests would still
     * belong to the account being left, so they would be thrown away and its data would stay put. */
    if (shouldReloadAccount(previousTenantId, nextTenantId)) {
      setAccountEpoch((epoch) => epoch + 1)
    }
  }, [isAuthenticated, user])

  if (modalProps) {
    if (modalProps.type === ModalType.auth0) {
      if (modalProps.variant === ModalVariant.delete) {
        sx = { width: '447px' }
        content = <DeleteIntegration type="Auth0" url="/integrations/auth0" />
      } else {
        content = <Auth0Integration variant={modalProps.variant} />
      }
    }
    if (modalProps.type === ModalType.crowdstrike) {
      if (modalProps.variant === ModalVariant.delete) {
        sx = { width: '600px' }
        const integration = configuredIntegrations.find((n) => n.integration_name === 'crowdstrike')
        const url = location.pathname.includes('library')
          ? `/api/v3/cpis/${modalProps?.props?.cpiName || ''}`
          : `/api/v3/integrations/crowdstrike/config/${integration?.id}`
        content = (
          <DeleteIntegration
            type="Crowdstrike"
            url={url}
            description={
              location.pathname.includes('library')
                ? 'If you delete this integration, all information you’ve provided will be deleted and your CPI may be left without a data source. Type “DELETE” to confirm:'
                : undefined
            }
          />
        )
      } else {
        sx = { width: '1128px' }
        content = <CrowdstrikeIntegration />
      }
    }
    if (modalProps.type === ModalType.mssentinel) {
      if (modalProps.variant === ModalVariant.delete) {
        sx = { width: '600px' }
        const integration = configuredIntegrations.find((n) => n.integration_name === 'mssentinel')
        const url = location.pathname.includes('library')
          ? `/api/v3/cpis/${modalProps?.props?.cpiName || ''}`
          : `/api/v3/integrations/mssentinel/config/${integration?.id}`
        content = (
          <DeleteIntegration
            type="Sentinel"
            url={url}
            description={
              location.pathname.includes('library')
                ? 'If you delete this integration, all information you’ve provided will be deleted and your CPI may be left without a data source. Type “DELETE” to confirm:'
                : undefined
            }
          />
        )
      } else {
        sx = { width: '1128px' }
        content = <SentinelIntegration />
      }
    }
    if (modalProps.type === ModalType.jira) {
      if (modalProps.variant === ModalVariant.delete) {
        sx = { width: '447px' }
        content = <DeleteIntegration type="Jira" url="/api/v3/integrations/jira" />
      } else {
        sx = { width: '952px' }
        content = <JiraIntegration variant={modalProps.variant} />
      }
    }
    if (modalProps.type === ModalType.jiraTicket) {
      if (modalProps.variant === ModalVariant.delete) {
        sx = { width: '447px' }
        content = <DeleteTicket />
      }
    }
    if (modalProps.type === ModalType.inviteUser) {
      sx = { width: '560px' }
      paperStyles = {
        background: theme.colors.modalSurface,
        boxShadow: theme.attributes.boxShadow.secondary,
        borderRadius: theme.attributes.borderRadius.quaternary
      }
      content = <InviteUser />
    }
    if (modalProps.type === ModalType.deleteUsers) {
      sx = { width: '833px' }
      paperStyles = {
        background: theme.colors.modalSurface,
        boxShadow: theme.attributes.boxShadow.secondary,
        borderRadius: theme.attributes.borderRadius.quaternary
      }
      content = <DeleteUsers />
    }
    if (modalProps.type === ModalType.changeName) {
      sx = { width: '560px' }
      paperStyles = {
        background: theme.colors.modalSurface,
        boxShadow: theme.attributes.boxShadow.secondary,
        borderRadius: theme.attributes.borderRadius.quaternary
      }
      content = <ChangeName />
    }
    if (modalProps.type === ModalType.changeEmail) {
      sx = { width: '560px' }
      paperStyles = {
        background: theme.colors.modalSurface,
        boxShadow: theme.attributes.boxShadow.secondary,
        borderRadius: theme.attributes.borderRadius.quaternary
      }
      content = <ChangeEmail />
    }
    if (modalProps.type === ModalType.changeRole) {
      sx = { width: '560px' }
      paperStyles = {
        background: theme.colors.modalSurface,
        boxShadow: theme.attributes.boxShadow.secondary,
        borderRadius: theme.attributes.borderRadius.quaternary
      }
      content = <ChangeRole />
    }
    if (modalProps.type === ModalType.transferOwnership) {
      sx = { width: '876px' }
      paperStyles = {
        background: theme.colors.modalSurface,
        boxShadow: theme.attributes.boxShadow.secondary,
        borderRadius: theme.attributes.borderRadius.quaternary
      }
      content = <TransferOwnership />
    }
    if (modalProps.type === ModalType.deleteAccount) {
      sx = { width: '833px' }
      paperStyles = {
        background: theme.colors.modalSurface,
        boxShadow: theme.attributes.boxShadow.secondary,
        borderRadius: theme.attributes.borderRadius.quaternary
      }
      content = <DeleteAccount />
    }
    if (modalProps.type === ModalType.deleteIntegrationInstance) {
      sx = { width: '784px' }
      paperStyles = {
        background: theme.colors.primaryDark,
        boxShadow: theme.attributes.boxShadow.secondary,
        borderRadius: theme.attributes.borderRadius.secondary
      }
      content = <DeleteIntegrationInstance />
    }
    if (modalProps.type === ModalType.deleteApplicationInstance) {
      sx = { width: '784px' }
      paperStyles = {
        background: theme.colors.primaryDark,
        boxShadow: theme.attributes.boxShadow.secondary,
        borderRadius: theme.attributes.borderRadius.secondary
      }
      content = <DeleteApplicationInstance />
    }
    if (modalProps.type === ModalType.deactivateCpi) {
      sx = { width: '582px' }
      paperStyles = {
        background: theme.colors.primaryDark,
        boxShadow: theme.attributes.boxShadow.secondary,
        borderRadius: theme.attributes.borderRadius.secondary
      }
      content = <DeactivateCpi />
    }
    if (modalProps.type === ModalType.deleteDeactivation) {
      sx = { width: '784px' }
      paperStyles = {
        background: 'transparent',
        boxShadow: 'none',
        borderRadius: theme.attributes.borderRadius.secondary
      }
      content = <DeleteDeactivate />
    }
    if (modalProps.type === ModalType.ssoSupport) {
      sx = { width: '570px' }
      paperStyles = {
        background: theme.colors.primaryDark,
        boxShadow: theme.attributes.boxShadow.secondary,
        borderRadius: theme.attributes.borderRadius.secondary,
        padding: `${theme.spacing.medium}px`,
        border: `1px solid ${theme.colors.controlBorder}`
      }
      content = <UpdateSSoModal />
    }
    if (modalProps.type === ModalType.ssoVerificationFailed) {
      sx = { width: '475px' }
      paperStyles = {
        background: theme.colors.primaryDark,
        boxShadow: theme.attributes.boxShadow.secondary,
        borderRadius: theme.attributes.borderRadius.secondary
      }
      content = <SSoVerificationFailed />
    }
    if (modalProps.type === ModalType.ssoDelete) {
      sx = { width: '475px' }
      paperStyles = {
        background: theme.colors.primaryDark,
        boxShadow: theme.attributes.boxShadow.secondary,
        borderRadius: theme.attributes.borderRadius.secondary
      }
      content = <SSoDeleteNotification />
    }
    if (modalProps.type === ModalType.cancelChangesWeight) {
      sx = { width: '761px' }
      paperStyles = {
        background: theme.colors.primaryDark,
        boxShadow: theme.attributes.boxShadow.secondary,
        borderRadius: theme.attributes.borderRadius.secondary
      }
      content = <CancelChanges />
    }
    if (modalProps.type === ModalType.cancelSetupIntegrationChanges) {
      sx = { width: '640px', height: '480px', hideZIndex: true }
      paperStyles = {
        background: theme.colors.modalSurface,
        boxShadow: theme.attributes.boxShadow.secondary,
        borderRadius: theme.attributes.borderRadius.secondary
      }
      content = <LeaveSetupIntegrations />
    }
    if (modalProps.type === ModalType.confirmDeleteFramework) {
      sx = { width: '480px', height: '320px' }
      paperStyles = {
        background: theme.colors.modalSurface,
        boxShadow: theme.attributes.boxShadow.secondary,
        borderRadius: theme.attributes.borderRadius.secondary
      }
      content = <ConfirmDeleteFramework />
    }
    if (modalProps.type === ModalType.addCpisToIntegration) {
      sx = { width: '640px', height: '480px' }
      paperStyles = {
        background: theme.colors.modalSurface,
        boxShadow: theme.attributes.boxShadow.secondary,
        borderRadius: theme.attributes.borderRadius.secondary
      }
      content = <AddCpisToIntegration />
    }
    if (modalProps.type === ModalType.activateCpi) {
      sx = { width: '664px', height: '316px' }
      paperStyles = {
        background: theme.colors.surfaceContainerBackground,
        boxShadow: theme.attributes.boxShadow.secondary,
        borderRadius: theme.attributes.borderRadius.tertiary
      }
      content = <ActivateCpiModal />
    }
    if (modalProps.type === ModalType.confirmResetFramework) {
      sx = { width: '480px', height: '320px' }
      paperStyles = {
        background: theme.colors.modalSurface,
        boxShadow: theme.attributes.boxShadow.secondary,
        borderRadius: theme.attributes.borderRadius.secondary
      }
      content = <ConfirmResetFramework />
    }
    if (modalProps.type === ModalType.disconnectJiraProject) {
      sx = { width: '480px', height: '320px' }
      paperStyles = {
        background: theme.colors.modalSurface,
        boxShadow: theme.attributes.boxShadow.secondary,
        borderRadius: theme.attributes.borderRadius.secondary
      }
      content = <DisconnectJiraProject />
    }
    if (modalProps.type === ModalType.revertDefaultValuesWeight) {
      sx = { width: '761px' }
      paperStyles = {
        background: theme.colors.primaryDark,
        boxShadow: theme.attributes.boxShadow.secondary,
        borderRadius: theme.attributes.borderRadius.secondary
      }
      content = <RevertWeights />
    }
    if (modalProps.type === ModalType.updateWeights) {
      sx = { width: '761px' }
      paperStyles = {
        background: theme.colors.primaryDark,
        boxShadow: theme.attributes.boxShadow.secondary,
        borderRadius: theme.attributes.borderRadius.secondary
      }
      content = <UpdateWeights />
    }
    if (modalProps.type === ModalType.deselectNotification) {
      sx = { width: '540px' }
      paperStyles = {
        background: theme.colors.primaryDark,
        boxShadow: theme.attributes.boxShadow.secondary,
        borderRadius: theme.attributes.borderRadius.secondary
      }
      content = <DeselectNotification />
    }
    if (modalProps.type === ModalType.userProfile) {
      sx = { width: '560px' }
      paperStyles = {
        background: theme.colors.modalSurface,
        boxShadow: theme.attributes.boxShadow.secondary,
        borderRadius: theme.attributes.borderRadius.quaternary,
        scrollBehavior: 'smooth',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',

        '&::-webkit-scrollbar': {
          display: 'none'
        }
      }
      content = <UserProfile />
    }
    if (modalProps.type === ModalType.zoomIn) {
      sx = { width: '1100px' }
      paperStyles = {
        background: theme.colors.modalSurface,
        boxShadow: theme.attributes.boxShadow.secondary,
        borderRadius: theme.attributes.borderRadius.quaternary
      }
      content = <ZoomIn />
    }
    if (modalProps.type === ModalType.nexaAIToggle) {
      sx = { width: '664px' }
      paperStyles = {
        background: theme.colors.backgroundDark,
        borderRadius: theme.attributes.borderRadius.quaternary
      }
      content = <NexaAIToggleModal />
    }
    if (modalProps.type === ModalType.calculationDetails) {
      sx = { width: '1464px' }
      paperStyles = {
        background: theme.colors.surfaceContainerBackground,
        borderRadius: theme.attributes.borderRadius.tertiary
      }
      content = <CalculationDetails />
    }
    if (modalProps.type === ModalType.benchmarkingDetails) {
      sx = { width: '1464px' }
      paperStyles = {
        background: theme.colors.surfaceContainerBackground,
        borderRadius: theme.attributes.borderRadius.tertiary
      }
      content = <BenchmarkingDetails />
    }
    if (modalProps.type === ModalType.createExclusionRule) {
      /* A panel, as the design has it: the rule is written against the records behind it. */
      drawer = true
      sx = { width: '720px' }
      paperStyles = {
        background: theme.colors.surfaceContainerBackground,
        boxShadow: theme.attributes.boxShadow.secondary,
        overflow: 'hidden'
      }
      content = <CreateExclusionRule />
    }
    if (modalProps.type === ModalType.createTicket) {
      sx = { width: '873px', maxWidth: '96vw' }
      paperStyles = {
        background: theme.colors.surfaceContainerBackground,
        boxShadow: theme.attributes.boxShadow.secondary,
        borderRadius: theme.attributes.borderRadius.quaternary,
        height: '686px',
        maxHeight: '92vh',
        overflow: 'hidden'
      }
      content = <CreateTicket />
    }
    if (modalProps.type === ModalType.excludedRecords) {
      sx = { width: '1260px', maxWidth: '95vw' }
      paperStyles = {
        background: theme.colors.modalSurface,
        boxShadow: theme.attributes.boxShadow.secondary,
        borderRadius: theme.attributes.borderRadius.quaternary,
        height: '700px',
        maxHeight: '95vh',
        overflow: 'hidden'
      }
      content = <ExcludedRecords />
    }
    if (modalProps.type === ModalType.deleteExclusionRule) {
      sx = { width: '664px' }
      paperStyles = {
        background: theme.colors.surfaceContainerBackground,
        boxShadow: theme.attributes.boxShadow.secondary,
        borderRadius: theme.attributes.borderRadius.quaternary,
        height: '292px',
        overflow: 'hidden'
      }
      content = <DeleteExclusionRule />
    }
  }

  const pageSize = () => {
    const body = document.getElementsByTagName('body')
    if (location.pathname.includes('/library/cpis') || isIntegrationsSubPath(location.pathname)) {
      body[0].style.overflow = 'auto'
    } else body[0].style.overflow = 'hidden'
  }

  pageSize()

  return (
    <ThemeProvider theme={muiTheme}>
      <QueryClientProvider client={queryClient}>
        <ReactQueryDevtools initialIsOpen={false} position="bottom-right" />
        <CssBaseline />
        <Box
          className="app-main"
          sx={{
            m: 0,
            p: 0,
            height: '100%',
            overflowY:
              location.pathname === '/dashboard' ||
              location.pathname.startsWith('/library') ||
              location.pathname === '/asset-inventory'
                ? 'hidden'
                : 'auto',

            '::-webkit-scrollbar': {
              background: '#6a6a6a',
              width: '12px !important',
              border: 'solid 6px transparent',
              borderRadius: '8px'
            },

            '::-webkit-scrollbar-track': {
              boxShadow: 'none',
              border: 'solid 6px transparent'
            },

            '::-webkit-scrollbar-thumb': {
              border: '4px solid rgba(0, 0, 0, 0)',
              backgroundClip: 'padding-box',
              borderRadius: '9999px',
              backgroundColor: theme.colors.borderMedium
            }
          }}
        >
          <>
            <Toaster
              position="bottom-right"
              containerClassName="react-hot-toast-container-outer"
              toastOptions={{
                duration: 5000,
                className: 'react-hot-toast-container',
                style: {
                  borderRadius: theme.attributes.borderRadius.tertiary,
                  background: theme.colors.textPrimary,
                  boxShadow: theme.colors.tooltipShadow,
                  padding: '16px',
                  gap: '16px',
                  width: '440px',
                  minHeight: '58px',
                  alignItems: 'center'
                },
                success: {
                  className: 'hot-toast-success',
                  style: {
                    background: '#7A9D52'
                  },
                  icon: (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      style={{ width: 24, height: 24, minWidth: 24, minHeight: 24, flexShrink: 0 }}
                    >
                      <path
                        d="M9 12.75L11.25 15L15 9.75M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                        stroke="#E9E0E4"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )
                },
                error: {
                  className: 'react-hot-toast-container hot-toast-error',
                  style: {
                    background: `${theme.colors.errorLight}`
                  }
                },
                blank: {
                  className: 'hot-toast-warning',
                  style: {
                    background: theme.colorTokens.info.warningHigh
                  },
                  icon: (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      style={{ width: 24, height: 24, minWidth: 24, minHeight: 24, flexShrink: 0 }}
                    >
                      <path
                        d="M12 8.25V12.75M12 15.75H12.0075M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                        stroke={theme.baseColors.surface[20]}
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )
                }
              }}
            />
            <Notification message={message} title={title} variant={variant} open={open} autoHideDelay={autoHideDelay} />
            <Modal
              content={content}
              sx={sx}
              paperStyles={paperStyles}
              disableCloseOnClickOutside={disableCloseOnClickOutside}
              drawer={drawer}
            />
            <MainLayout
              user={{ name: user?.name ?? '', profileURL: user?.profileImage ?? '' }}
              accountEpoch={accountEpoch}
            />
          </>
        </Box>
      </QueryClientProvider>
    </ThemeProvider>
  )
}

export default App
